import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Truck, Receipt, Users, Building2, BarChart3,
  Settings as SettingsIcon, Search, LogOut, ShieldCheck, Menu, X, ChevronRight, PackageCheck, PackageOpen, Boxes,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const NAV = [
  { to: "/admin/warranty", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/warranty/claims", label: "Warranty Claims", icon: FileText },
  { to: "/admin/vendor-claims", label: "Vendor Claims", icon: Truck },
  { to: "/admin/ready-to-ship", label: "Ready to Ship", icon: PackageOpen },
  { to: "/admin/replacements", label: "Replacements", icon: PackageCheck },
  { to: "/admin/credit-notes", label: "Credit Notes", icon: Receipt },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/suppliers", label: "Suppliers", icon: Building2 },
  { to: "/admin/sub-inventory", label: "Sub Inventory", icon: Boxes },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q)}`);
        setResults(data.results);
        setOpen(true);
      } catch {}
    }, 250);
  }, [q]);

  return (
    <div ref={ref} className="relative w-full max-w-md">
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search RMA, serial, order, customer, CN..."
          className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
        />
      </div>
      {open && results.length > 0 && (
        <div data-testid="global-search-results" className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          {results.map((r) => (
            <button
              key={`${r.type}-${r.id}`}
              onClick={() => { navigate(r.link); setOpen(false); setQ(""); }}
              className="flex w-full items-center justify-between border-b border-slate-50 px-4 py-2.5 text-left hover:bg-slate-50"
            >
              <div>
                <div className="font-mono text-sm font-semibold text-slate-900">{r.title}</div>
                <div className="text-xs text-slate-500">{r.subtitle}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const roleLabel = (user?.role || "").replace(/_/g, " ");

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-[#0F172A] transition-transform lg:static lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center gap-2.5 px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display text-sm font-extrabold tracking-tight text-white">IDEA</div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">RMA System</div>
          </div>
        </div>
        <nav className="mt-4 space-y-1 px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-[#1E293B] hover:text-white"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-slate-800 p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
              {(user?.name || "?").charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-white">{user?.name}</div>
              <div className="truncate text-[11px] uppercase tracking-wide text-slate-400">{roleLabel}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-[#1E293B] hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-slate-200 bg-white/80 px-4 backdrop-blur lg:px-8">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <GlobalSearch />
          <div className="ml-auto hidden items-center gap-2 sm:flex">
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">Live</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">
          {title && <h1 className="mb-6 font-display text-2xl font-extrabold tracking-tight text-slate-900 lg:text-3xl">{title}</h1>}
          {children}
        </main>
      </div>
    </div>
  );
}
